import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import DatePicker from "react-multi-date-picker";
import {
  Dialog,
  Button,
  TextInput,
  Dropdown,
  DropdownWithSearch,
  CustomCheckBox,
  Image,
} from "hesus-ui-kit";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import ClipLoader from "react-spinners/ClipLoader";
import * as moment from "moment";
import {
  useGetGeneralParameters,
  useGetMultiUserCurrenciesByCountryId,
} from "@hooks/useGeneralParams";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import closeIcon from "@icons/ic-close-white.svg";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import { packagingTypeText } from "@utils/constants";
import Department from "./components/Department/Department";
import AddEditZone from "./components/AddEditZone/AddEditZone";

import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";

import {
  getCenterProductPriceTypeOptions,
  getCenterProductPriceCatchmentTypeOptions,
  updateUnitTranslation,
} from "@utils/utils";

import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  SectionContainer,
  OuterWrapper,
  FlexRow,
  ContainerLabel,
  GridRow,
  MarginDiv,
  CheckboxGridRow,
  RequiredMessage,
} from "./AddEditCenterProductPrice.styled";
import "./AddEditCenterProductPrice.scss";

const AddEditCenterProductPrice = (props) => {
  const {
    t,
    id,
    actionType,
    title,
    onClose,
    subCategoryId,
    updateRefeshStatus,
    transportTypes,
    defaultUserCurrency,
    companyId,
    centerId,
    centerProductId,
    countryId,
  } = props;
  const [isCenterProductPriceCreating, setIsCenterProductPriceCreating] =
    useState(false);
  const [createProductPriceAPIError, setCreateProductPriceAPIError] =
    useState("");

  const [currencies, setCurrencies] = useState([]);

  const priceListByIds = useSelector(
    (state) => state.centerProductPriceList.byId
  );
  const generalParams = useGetGeneralParameters();
  const { unitList } = generalParams;
  const countryUserCurrencies = useGetMultiUserCurrenciesByCountryId(countryId);
  useEffect(() => {
    if (
      currencies.length === 0 &&
      Object.entries(countryUserCurrencies)?.length > 0
    ) {
      const list = Object.entries(countryUserCurrencies).map((entry) => ({
        label: entry[1],
        value: parseInt(entry[0], 10),
      }));
      setCurrencies(list);
    }
  }, [currencies, countryUserCurrencies]);

  const selectedPrice = priceListByIds[id];

  const [formData, setFormData] = useState({
    label: {
      required: false,
      value: selectedPrice?.label ?? "",
      error: "",
    },
    priceType: {
      required: true,
      value: selectedPrice?.priceType ?? "",
      error: "",
    },
    isMemoryPrice: {
      required: false,
      value: selectedPrice?.isMemoryPrice ?? "",
      error: "",
    },
    price: {
      required: true,
      value: selectedPrice?.price ?? "",
      error: "",
    },
    unit: {
      required: true,
      value: selectedPrice?.unit ?? "",
      error: "",
    },
    currency: {
      required: true,
      value:
        actionType === "create"
          ? defaultUserCurrency
          : selectedPrice?.currency?.id,
      error: "",
    },
    transportTypes: {
      required: true,
      value: selectedPrice?.transportTypes ?? [],
      error: "",
    },
    validityStartDate: {
      required: true,
      value: selectedPrice?.validityStartDate ?? "",
      error: "",
    },
    validityEndDate: {
      required: false,
      value: selectedPrice?.validityEndDate ?? "",
      error: "",
    },
    packagingTypes: {
      required: false,
      value: selectedPrice?.packagingTypes ?? [],
      error: "",
    },
    tax: {
      required: false,
      value: selectedPrice?.tax ?? "",
      error: "",
    },
    priceDetails: {
      required: false,
      value: selectedPrice?.priceDetails ?? "",
      error: "",
    },
    catchmentType: {
      required: true,
      value: selectedPrice?.catchmentArea?.catchmentType ?? "",
      error: "",
    },
    catchmentValue: {
      required: true,
      value:
        selectedPrice?.catchmentArea?.catchmentType === 1
          ? selectedPrice?.catchmentArea?.catchmentValue
          : "",
      error: "",
    },
    department: {
      required: true,
      value:
        selectedPrice?.catchmentArea?.catchmentType === 2
          ? selectedPrice?.catchmentArea?.catchmentValue ?? ""
          : "",
      error: "",
    },
    zone: {
      required: true,
      value:
        selectedPrice?.catchmentArea?.catchmentType === 3
          ? selectedPrice?.catchmentArea?.catchmentValue ?? ""
          : "",
      error: "",
    },
    catchmentTechnicalInfo: {
      required: false,
      value: selectedPrice?.catchmentArea?.catchmentTechnicalInfo ?? "",
      error: "",
    },
  });

  const [descriptionData, setDescriptionData] = useState(
    selectedPrice?.priceDetails ?? ""
  );

  const [technicalInfoData, setTechnicalInfoData] = useState(
    selectedPrice?.catchmentArea?.catchmentTechnicalInfo ?? ""
  );

  const [catchmentValueData, setCatchmentValueData] = useState(
    selectedPrice?.catchmentArea?.catchmentValue ?? ""
  );
  const [departmentData, setDepartmentData] = useState(
    selectedPrice?.catchmentArea?.catchmentType === 2
      ? selectedPrice?.catchmentArea?.catchmentValue ?? ""
      : ""
  );
  const [zoneData, setZoneData] = useState(
    selectedPrice?.catchmentArea?.catchmentType === 3
      ? selectedPrice?.catchmentArea?.catchmentValue ?? ""
      : ""
  );

  useEffect(() => {
    setFormData({
      ...formData,
      catchmentValue: {
        value: catchmentValueData,
        error: "",
        required: true,
      },
    });
  }, [catchmentValueData]);

  useEffect(() => {
    setFormData({
      ...formData,
      department: {
        value: departmentData,
        error: "",
        required: true,
      },
    });
  }, [departmentData]);

  useEffect(() => {
    setFormData({
      ...formData,
      zone: {
        value: zoneData,
        error: "",
        required: true,
      },
    });
  }, [zoneData]);

  useEffect(() => {
    setFormData({
      ...formData,
      catchmentTechnicalInfo: {
        value: technicalInfoData,
        error: "",
        required: true,
      },
    });
  }, [technicalInfoData]);

  useEffect(() => {
    setFormData({
      ...formData,
      priceDetails: {
        value: descriptionData,
        error: "",
        required: true,
      },
    });
  }, [descriptionData]);

  const onLabelChange = (e) => {
    setFormData({
      ...formData,
      label: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onPriceTypeChange = (e) => {
    setFormData({
      ...formData,
      priceType: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };
  const onMemoryChange = (e) => {
    setFormData({
      ...formData,
      isMemoryPrice: {
        value: !formData.isMemoryPrice.value,
        error: "",
        required: true,
      },
    });
  };
  const onPriceChange = (e) => {
    setFormData({
      ...formData,
      price: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onCurrencyChange = (e) => {
    setFormData({
      ...formData,
      currency: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onUnitChange = (e) => {
    setFormData({
      ...formData,
      unit: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onTransportTypeChange = (filter) => {
    setFormData({
      ...formData,
      transportTypes: {
        value: filter,
        error: "",
        required: true,
      },
    });
  };

  const onValidityStartDateChange = (e) => {
    setFormData({
      ...formData,
      validityStartDate: {
        value: moment(e.toDate()).format("YYYY-MM-DD"),
        error: "",
        required: true,
      },
    });
  };

  const onValidityEndDateChange = (e) => {
    let endDate = "";
    if (e !== null) {
      endDate = moment(e.toDate()).format("YYYY-MM-DD");
    }
    setFormData({
      ...formData,
      validityEndDate: {
        value: endDate,
        error: "",
        required: true,
      },
    });
  };

  const onTaxChange = (e) => {
    setFormData({
      ...formData,
      tax: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onCatchmenTypeChange = (e) => {
    setFormData({
      ...formData,
      catchmentType: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  const onCatchmentValueChange = (e) => {
    setCatchmentValueData(e.target.value);
  };

  const [packagingTypes, setPackagingTypes] = useState({
    type1: {
      status: false,
    },
    type2: {
      status: false,
    },
    type3: {
      status: false,
    },
  });

  const onRoleChange = (e, checkedRoleId) => {
    const jobValue = e.target.checked;
    if (checkedRoleId === "1") {
      setPackagingTypes({
        ...packagingTypes,
        type1: {
          status: jobValue,
        },
      });
    }
    if (checkedRoleId === "2") {
      setPackagingTypes({
        ...packagingTypes,
        type2: {
          status: jobValue,
        },
      });
    }
    if (checkedRoleId === "3") {
      setPackagingTypes({
        ...packagingTypes,
        type3: {
          status: jobValue,
        },
      });
    }
  };

  useEffect(() => {
    if (id) {
      const transports = [];
      if (formData.transportTypes.value.length) {
        formData.transportTypes.value.map((transport) => {
          transports.push({
            value: transport.id,
            label: transport.name,
          });
        });
      }
      setFormData({
        ...formData,
        validityStartDate: {
          value: selectedPrice.validityStartDate
            ? moment(selectedPrice.validityStartDate).format("YYYY-MM-DD")
            : null,
          error: "",
          required: true,
        },
        validityEndDate: {
          value: selectedPrice.validityEndDate
            ? moment(selectedPrice.validityEndDate).format("YYYY-MM-DD")
            : null,
          error: "",
          required: true,
        },
        transportTypes: {
          value: transports,
          error: "",
          required: true,
        },
      });

      const packs = [];
      if (selectedPrice.packagingTypes.length) {
        selectedPrice.packagingTypes.map((type) => {
          packs.push(parseInt(type.id, 10));
        });

        setPackagingTypes({
          ...packagingTypes,
          type1: {
            status: packs.includes(1),
          },
          type2: {
            status: packs.includes(2),
          },
          type3: {
            status: packs.includes(3),
          },
        });
      }
    }
  }, [id]);

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.priceType.value) {
      isFormValid = false;
      data.priceType.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.price.value && data.price.value !== 0) {
      isFormValid = false;
      data.price.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (data.price.value && !/^-?\d*[.,]?\d{0,2}$/.test(data.price.value)) {
      isFormValid = false;
      data.price.error = t("GENERAL_FORMAT_ERROR");
    }
    if (!data.unit.value) {
      isFormValid = false;
      data.unit.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.currency.value) {
      isFormValid = false;
      data.currency.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (data.transportTypes.value.length === 0) {
      isFormValid = false;
      data.transportTypes.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.validityStartDate.value) {
      isFormValid = false;
      data.validityStartDate.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (data.catchmentType.value === "") {
      isFormValid = false;
      data.catchmentType.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.catchmentValue.value && data.catchmentType.value === 1) {
      isFormValid = false;
      data.catchmentValue.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (data.catchmentType.value === 2 && !data.department.value) {
      isFormValid = false;
      data.department.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (data.catchmentType.value === 3 && !data.zone.value) {
      isFormValid = false;
      data.zone.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const getFormData = () => {
    const packTypes = [];
    if (packagingTypes.type1.status) {
      packTypes.push({
        id: 1,
        name: packagingTypeText[1],
      });
    }
    if (packagingTypes.type2.status) {
      packTypes.push({
        id: 2,
        name: packagingTypeText[2],
      });
    }
    if (packagingTypes.type3.status) {
      packTypes.push({
        id: 3,
        name: packagingTypeText[3],
      });
    }

    const transports = [];
    if (formData.transportTypes.value.length) {
      formData.transportTypes.value.map((transport) => {
        transports.push({
          id: transport.value,
          name: transport.label,
        });
      });
    }

    let finalCatchmentValue = "";
    if (formData.catchmentType.value === 2)
      finalCatchmentValue = formData.department.value;
    else if (formData.catchmentType.value === 3)
      finalCatchmentValue = formData.zone.value;
    else if (formData.catchmentType.value !== 0)
      finalCatchmentValue = formData.catchmentValue.value;

    const data = {
      id: actionType === "edit" ? selectedPrice.id : "",
      priceType: formData.priceType.value,
      centreProductId: centerProductId,
      label: formData.label.value,
      tax: formData.tax.value,
      isActive: true,
      price: formData.price.value
        ? Number(String(formData.price.value).replace(",", "."))
        : 0,
      unit: formData.unit.value,
      currency: {
        id: formData.currency.value,
      },
      isMemoryPrice: formData.isMemoryPrice.value,
      validityStartDate: formData.validityStartDate.value,
      validityEndDate: formData.validityEndDate.value,
      priceDetails: formData.priceDetails.value,
      transportTypes: transports,
      packagingTypes: packTypes,
      catchmentArea: {
        catchmentType: formData.catchmentType.value,
        catchmentValue: finalCatchmentValue,
        catchmentTechnicalInfo: formData.catchmentTechnicalInfo.value,
      },
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      const data = getFormData(formData);
      setIsCenterProductPriceCreating(true);
      setCreateProductPriceAPIError("");
      // Create and copy price
      if ((!id && actionType === "create") || (id && actionType === "copy")) {
        axios
          .post(
            `/companies/${companyId}/centres/${centerId}/products/${centerProductId}/prices`,
            data
          )
          .then(() => {
            setIsCenterProductPriceCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsCenterProductPriceCreating(false);
            setCreateProductPriceAPIError(error.response?.data?.message ?? "");
          });
      }
      // Edit
      if (id && actionType === "edit") {
        axios
          .put(
            `/companies/${companyId}/centres/${centerId}/products/${centerProductId}/prices/${id}`,
            data
          )
          .then(() => {
            setIsCenterProductPriceCreating(false);
            updateRefeshStatus();
            // onClose();
          })
          .catch((error) => {
            setIsCenterProductPriceCreating(false);
            setCreateProductPriceAPIError(error.response?.data?.message ?? "");
          });
      }
    }
  };

  return (
    <Dialog>
      <div>
        <HeaderWrapper>
          <Title>{t(title)}</Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image src={closeIcon} alt="close Analytical Note price popup" />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OuterWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr">
                    <TextInput
                      type="text"
                      label={t("CENTRE_PRODUCT_PRICE_LABEL")}
                      name="center-product-price-title"
                      id="center-product-price-title"
                      value={String(formData.label?.value) || ""}
                      onChange={onLabelChange}
                      isFloat
                      labelStyles={labelStyles}
                      containerStyles={{ marginTop: "21px" }}
                      inputStyles={{ padding: "7px 5px 7px 10px" }}
                      error={formData.label.error}
                      placeholder={t("CENTRE_PRODUCT_PRICE_LABEL")}
                    />
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <MarginDiv marginInfo="margin-right:20px;">
                      <Dropdown
                        id="price-types"
                        label={t("PRICE_TYPE")}
                        name="priceType"
                        value={formData.priceType.value}
                        onChange={onPriceTypeChange}
                        defaultOption={t("PRICE_TYPE")}
                        options={getCenterProductPriceTypeOptions(t)}
                        labelStyles={
                          formData.priceType.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputStyles={
                          formData.priceType.error !== ""
                            ? inputStylesError
                            : inputStyles
                        }
                        required
                        error={formData.priceType.error}
                      />
                    </MarginDiv>
                    <MarginDiv
                      className="status__filters-wrapper"
                      marginInfo="margin-left:20px;"
                    >
                      <Image
                        src={
                          formData.isMemoryPrice.value
                            ? enableIcon
                            : disableIcon
                        }
                        onClick={onMemoryChange}
                      />{" "}
                      <ContainerLabel>{t("MEMORY_PRICE")}</ContainerLabel>
                    </MarginDiv>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <MarginDiv marginInfo="margin-right:40px;">
                      <TextInput
                        type="text"
                        label={t("PRICE")}
                        name="center-product-price-price"
                        id="center-product-price-price"
                        isFloat
                        value={String(formData.price.value)}
                        onChange={onPriceChange}
                        containerStyles={{ marginTop: "21px" }}
                        labelStyles={
                          formData.price.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputStyles={
                          formData.price.error !== ""
                            ? inputStylesError
                            : inputStyles
                        }
                        required
                        error={formData.price.error}
                        placeholder={t("PRICE")}
                      />
                    </MarginDiv>

                    <MarginDiv marginInfo="margin-left:20px;">
                      <GridRow column="1fr 1fr">
                        <Dropdown
                          id="center-product-price-currency"
                          label={t("GENERAL_CURRENCY")}
                          name="priceType"
                          value={formData.currency.value}
                          onChange={onCurrencyChange}
                          defaultOption={t("SELECT_CURRENCY")}
                          options={currencies}
                          labelStyles={
                            formData.currency.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            formData.currency.error !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          required
                          error={formData.currency.error}
                        />
                        <Dropdown
                          id="center-product-price-unit"
                          label={t("UNIT")}
                          name="unit"
                          value={formData.unit.value}
                          onChange={onUnitChange}
                          defaultOption={t("UNIT")}
                          options={updateUnitTranslation(unitList, t)}
                          labelStyles={
                            formData.unit.error !== ""
                              ? labelStylesError
                              : labelStyles
                          }
                          inputStyles={
                            formData.unit.error !== ""
                              ? inputStylesError
                              : inputStyles
                          }
                          required
                          error={formData.unit.error}
                        />
                      </GridRow>
                    </MarginDiv>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <MarginDiv marginInfo="margin-right:40px;">
                      <DropdownWithSearch
                        id="transport-types"
                        label={t("TRANSPORT_TYPES")}
                        placeholder={t("TRANSPORT_TYPES")}
                        name="transportType"
                        labelClass={
                          formData.transportTypes?.error !== ""
                            ? "center-product-price__transport-type-error"
                            : "center-product-price__transport-type"
                        }
                        value={
                          formData.transportTypes?.value.length > 0
                            ? formData.transportTypes.value
                            : null
                        }
                        defaultOption={null}
                        onChange={onTransportTypeChange}
                        options={transportTypes}
                        ismulti
                        labelStyles={
                          formData.transportTypes.error !== ""
                            ? labelStylesError
                            : labelStyles
                        }
                        inputClass={
                          formData.transportTypes.error !== ""
                            ? "center-product-price__dropdown-input-error"
                            : "center-product-price__dropdown-input"
                        }
                        required
                        error={formData.transportTypes.error}
                      />
                      {formData.transportTypes.error && (
                        <RequiredMessage>
                          {formData.transportTypes.error}
                        </RequiredMessage>
                      )}
                    </MarginDiv>
                    <MarginDiv marginInfo="margin-left:20px;">
                      <GridRow column="1fr 1fr">
                        <div
                          className={
                            formData.validityStartDate.error
                              ? "center-product-price__date-container_start_error"
                              : "center-product-price__date-container_start"
                          }
                        >
                          <label htmlFor="winning-date">
                            {t("CENTER_PRODUCT_VALIDITY_DATE_START")}
                          </label>
                          <DatePicker
                            inputClass={
                              formData.validityStartDate.error
                                ? "center-product-price__date-input-error"
                                : "center-product-price__date-input"
                            }
                            containerClassName="center-product-price__datepicker-container"
                            type="text"
                            label={t("CENTER_PRODUCT_VALIDITY_DATE_START")}
                            name="center-product-price-start-date"
                            id="center-product-price-start-date"
                            calendarPosition="bottom-center"
                            isFloat
                            onChange={onValidityStartDateChange}
                            format={getDatePickerFormat()}
                            locale={getCalendarLocale()}
                            value={
                              getDateFormat(
                                formData.validityStartDate.value,
                                "YYYY-MM-DD"
                              ) || ""
                            }
                            containerStyles={{ marginTop: "21px" }}
                            labelStyles={
                              formData.validityStartDate.error !== ""
                                ? labelStylesError
                                : labelStyles
                            }
                            required
                            error={formData.validityStartDate.error}
                            placeholder={t(
                              "CENTER_PRODUCT_VALIDITY_DATE_START"
                            )}
                          />
                          {formData.validityStartDate.error && (
                            <RequiredMessage>
                              {formData.validityStartDate.error}
                            </RequiredMessage>
                          )}
                        </div>
                        <div className="center-product-price__date-container_end">
                          <label htmlFor="winning-date">
                            {t("CENTER_PRODUCT_VALIDITY_DATE_END")}
                          </label>
                          <DatePicker
                            inputClass="center-product-price__date-input"
                            containerClassName="center-product-price__datepicker-container"
                            type="text"
                            label={t("CENTER_PRODUCT_VALIDITY_DATE_END")}
                            name="center-product-price-end-date"
                            id="center-product-price-end-date"
                            calendarPosition="bottom-center"
                            isFloat
                            onChange={onValidityEndDateChange}
                            onKeyUp={onValidityEndDateChange}
                            format={getDatePickerFormat()}
                            locale={getCalendarLocale()}
                            value={
                              getDateFormat(
                                formData.validityEndDate.value,
                                "YYYY-MM-DD"
                              ) || ""
                            }
                            containerStyles={{ marginTop: "21px" }}
                            inputStyles={{ padding: "7px 5px 7px 10px" }}
                            error={formData.validityEndDate.error}
                            placeholder={t("CENTER_PRODUCT_VALIDITY_DATE_END")}
                          />
                        </div>
                      </GridRow>
                    </MarginDiv>
                  </GridRow>
                  <GridRow column="1fr">
                    <ContainerLabel>{t("PACKING_TYPE")}</ContainerLabel>
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <MarginDiv marginInfo="margin-right:40px;">
                      <CheckboxGridRow column="1fr 1fr 1fr">
                        <CustomCheckBox
                          id="package-1"
                          labelText={t("PACKAGING_TYPE_BULK")}
                          dataId="package-1"
                          onChange={(e) => {
                            onRoleChange(e, "1");
                          }}
                          checked={packagingTypes.type1.status}
                        />
                        <CustomCheckBox
                          id="package-2"
                          labelText={t("PACKAGING_TYPE_BIGBAG")}
                          dataId="package-2"
                          onChange={(e) => {
                            onRoleChange(e, "2");
                          }}
                          checked={packagingTypes.type2.status}
                        />
                        <CustomCheckBox
                          id="package-3"
                          labelText={t("PACKAGING_TYPE_BODYBENNE")}
                          dataId="package-3"
                          onChange={(e) => {
                            onRoleChange(e, "3");
                          }}
                          checked={packagingTypes.type3.status}
                        />
                      </CheckboxGridRow>
                    </MarginDiv>
                    <MarginDiv marginInfo="margin-left:20px; margin-top: -24px;">
                      {subCategoryId === 1 && (
                        <TextInput
                          type="text"
                          label={t("TGAP")}
                          name="center-product-price-start-date"
                          id="center-product-price-start-date"
                          isFloat
                          value={String(formData.tax.value)}
                          onChange={onTaxChange}
                          containerStyles={{ marginTop: "21px" }}
                          labelStyles={labelStyles}
                          inputStyles={{ padding: "7px 5px 7px 10px" }}
                          error={formData.tax.error}
                          placeholder={t("TGAP")}
                        />
                      )}
                    </MarginDiv>
                  </GridRow>
                  <GridRow column="1fr">
                    <ContainerLabel>
                      {t("CENTRE_PRODUCT_PRICE_DETAILS")}
                    </ContainerLabel>
                  </GridRow>
                  <GridRow column="1fr">
                    <CKEditorText
                      descriptionData={descriptionData}
                      setDescriptionData={setDescriptionData}
                    />
                    {formData.priceDetails.error && (
                      <RequiredMessage>
                        {formData.priceDetails.error}
                      </RequiredMessage>
                    )}
                  </GridRow>
                  <GridRow column="1fr 1fr">
                    <Dropdown
                      id="catchment-types"
                      label={t("CATCHMENT_AREA")}
                      name="catchmenType"
                      value={formData.catchmentType.value}
                      onChange={onCatchmenTypeChange}
                      defaultOption={t("CATCHMENT_AREA")}
                      options={getCenterProductPriceCatchmentTypeOptions(t)}
                      labelStyles={
                        formData.catchmentType.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.catchmentType.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.catchmentType.error}
                    />
                    {formData.catchmentType.value === 1 && (
                      <TextInput
                        type="text"
                        label={t("RADIUS")}
                        // name={selectedMeetingReport.title}
                        id="center-product-price-radius"
                        isFloat
                        value={String(formData.catchmentValue.value)}
                        onChange={onCatchmentValueChange}
                        containerStyles={{ marginTop: "21px" }}
                        inputStyles={{ padding: "7px 5px 7px 10px" }}
                        required
                        // disabled={isDisabled}
                        error={formData.catchmentValue.error}
                        placeholder={t("RADIUS")}
                      />
                    )}
                  </GridRow>
                  {formData.catchmentType.value === 2 && (
                    <Department
                      selectedDepartment={
                        formData.department.value !== ""
                          ? formData.department.value.split(",")
                          : []
                      }
                      error={formData.department.error}
                      setDepartmentCatchmentValueChange={setDepartmentData}
                      countryId={countryId}
                    />
                  )}
                  {formData.catchmentType.value === 3 && (
                    <AddEditZone
                      data={{
                        id,
                        catchmentTechnicalInfo:
                          formData.catchmentTechnicalInfo.value,
                        catchmentValue: formData.zone.value,
                      }}
                      error={formData.zone.error}
                      setCatchmentZone={setZoneData}
                      setTechnicalInfo={setTechnicalInfoData}
                      countryId={countryId}
                    />
                  )}
                </div>
              </SectionContainer>
            </FlexRow>
          </OuterWrapper>
        </FormBody>
        <ButtonWrapper>
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isCenterProductPriceCreating && (
                <ClipLoader size={15} color="#fff" />
              )}
              {!isCenterProductPriceCreating && t("SUBMIT")}
            </>
          </Button>
          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditCenterProductPrice.propTypes = {
  t: PropTypes.func.isRequired,
  defaultUserCurrency: PropTypes.number,
  countryId: PropTypes.number,
};

AddEditCenterProductPrice.defaultProps = {
  defaultUserCurrency: 0,
  countryId: null,
};

export default withTranslation()(AddEditCenterProductPrice);
